import paletteAsString from './paintPalette/paletteAsString';
import paletteConverter from './paintPalette/paletteConverter';

export default {
    namespaced: true,

    state: {
        palette: [],

        cachedBrushes: {}, // for prevent each time to cache the brush that was cached before

        // currentPaint is the palette item. Default is yellow
        currentPaint: {},

        paintSide: true, // sets by the user in the paint menu; true/false = outer/inner
        paintOffset: 35, // distance between trim lines and paint
    },

    getters: {
        getPaintById: (state) => (id) => {
            return state.palette.find(paint => paint.id === id);
        },

        sideCoefficient(state) {
            return state.paintSide ? 1 : -1;
        },

        getPaintParts: () => (points) => {
            let lastIndex = 0;
            let paintParts = [];

            points.forEach((point, index) => {
                if (point.type === 'fold') {
                    let newIndex = index + 1;
                    let part = points.slice(lastIndex, newIndex);

                    if (part.length > 1) {
                        // part must contain at least 2 points to create a line
                        paintParts.push(part);
                    }

                    lastIndex = newIndex;
                }
            });

            // add the last part
            if (points.length - 1 > lastIndex) {
                let part = points.slice(lastIndex);

                paintParts.push(part);
            }

            return paintParts;
        },

        getPaintLineStrokePoints: (state, getters, rootState, rootGetters) => (points) => {
            // the line stroke using the trim paint vertices
            const linePoints = getters.getPaintLineVertices(points);

            // gaps between brush strokes that make up the paint
            const linePointsDistanceBetween = 3;

            let pointsArray = [];

            // loop throw lines based on each two points
            for (let i = 1; i < linePoints.length; i++) {
                let p1 = linePoints[i-1];
                let p2 = linePoints[i];
                let lineAngle = rootGetters['line/getLineByPoints'](p1, p2).angle360;
                let rotation = lineAngle - getters.paintStartAngle; // rotation uses for the no-circle brush image

                let dx = p2.x - p1.x;
                let dy = p2.y - p1.y;

                let length;
                let j;

                // points on line
                // loop on biggest side
                if (Math.abs(dx) >= Math.abs(dy)) {

                    // get direction
                    if (dx >= 0) {
                        length = dx;
                        j = 0;
                    }
                    else {
                        length = 0;
                        j = dx;
                    }

                    for (j; j < length; j += linePointsDistanceBetween) {
                        let x = p1.x + j;
                        let y = getters.lineFormulaY(x, p1, dx, dy);
                        let p = {x, y, rotation};

                        pointsArray.push(p)
                    }
                }
                else {

                    // get direction
                    if (dy >= 0) {
                        length = dy;
                        j = 0;
                    }
                    else {
                        length = 0;
                        j = dy;
                    }

                    for (j; j < length; j += linePointsDistanceBetween) {
                        let y = p1.y + j;
                        let x = getters.lineFormulaX(y, p1, dx, dy);
                        let p = {x, y, rotation};

                        pointsArray.push(p)
                    }
                }

            }

            return pointsArray;
        },

        pointOnAngle: (state, getters, rootState, rootGetters) => (paintPrevPoint, p1, p2, p3, offset, /*sideCoefficient*/) => {
            // paintPrevPoint - previous paint point
            // p1, p2, p3 - trim points
            // p1 - startPoint
            // p2 - centerPoint
            // p3 - endPoint

            // temporary move the paintPrevPoint to a very short distance to the trim
            const SHORT_DISTANCE = 0.5;
            let paintPrevPointShortLine = rootGetters['line/getLineWithNewLength'](p1, paintPrevPoint, SHORT_DISTANCE);

            let paintPrevPointOnShortDistance = paintPrevPointShortLine.toPoint;

            // use paintPrevPointShortDiff point to get the angles diff, that show the angle type: obtuse/acute
            let trimAngle = rootGetters['angles/getAngles']([p1, p2, p3])[0];
            let paintAngle = rootGetters['angles/getAngles']([paintPrevPointOnShortDistance, p2, p3])[0];

            // point placed on the acute or obtuse angle side
            // 0 - obtuse angle
            // 180 - acute angle
            let angleDiff = trimAngle.angleSizeOrigin >= paintAngle.angleSizeOrigin ? 0 : 180;

            // point is placed on the center of the angle,
            // so need to take a half of the angle and add to the start angle,
            // plus angleDiff to acute angle
            let newAngle = angleDiff + trimAngle.angleDirectionStart + trimAngle.angleSize/2;

            // paint line is going parallel to the trim line on the "offset" distance
            let distance = offset / Math.sin((trimAngle.angleSize / 2) / 180 * Math.PI);

            let dx = distance * Math.cos(newAngle / 180 * Math.PI);
            let dy = distance * Math.sin(newAngle / 180 * Math.PI);

            // in case the line is continue of the previous, we don't know the correct angle,
            // because in this case 0deg===180deg, so here need to check the position diff to previous point
            if (trimAngle.angleSizeOrigin === 180) {
                let dxSideCoefficient = Math.sign(paintPrevPoint.x - p1.x);
                let dySideCoefficient = Math.sign(paintPrevPoint.y - p1.y);

                dx = Math.sign(dx) === dxSideCoefficient ? dx : dx * -1;
                dy = Math.sign(dy) === dySideCoefficient ? dy : dy * -1;
            }

            let paintNextPoint = {
                x: p2.x + dx,
                y: p2.y + dy,
            };

            return paintNextPoint;
        },

        pointOnEdge: (state, getters, rootState, rootGetters) => (p1, p2, sideCoefficient, offset) => {

            let firstOriginalLine = rootGetters['line/getLineByPoints'](p1, p2);

            // use (360 - angel) to get the opposite angle in the right triangle
            let angle = 360 - firstOriginalLine.angle360;

            // switch sin and cos for dx and dy because the right triangle angles (90 - angle)
            let dy = offset * Math.cos(angle / 180 * Math.PI);
            let dx = offset * Math.sin(angle / 180 * Math.PI);

            // switch side of paint
            dx *= sideCoefficient;
            dy *= sideCoefficient;

            let pointOnEdge = {
                x: p1.x + dx,
                y: p1.y + dy,
            };

            return pointOnEdge
        },

        startPoint: (state, getters) => (p1, p2, offset, sideCoefficient) => {
            return getters.pointOnEdge(p1, p2, sideCoefficient, offset);
        },

        endPoint: (state, getters) => (p1, p2, offset, sideCoefficient) => {
            const coefficient = sideCoefficient * -1;

            return getters.pointOnEdge(p1, p2, coefficient, offset)
        },

        getPaintLineVertices: (state, getters) => (points) => {
            const offset = state.paintOffset;
            const sideCoefficient = getters.sideCoefficient;

            const l = points.length;
            let paintPoints = [];

            let startPoint = getters.startPoint(points[0], points[1], offset, sideCoefficient);
            paintPoints.push(startPoint);

            let prevPoint = startPoint;

            for (let i = 2; i < points.length; i++) {
                let anglePointStart = points[i - 2];
                let anglePointCenter = points[i - 1];
                let anglePointEnd = points[i];
                let nextPoint = getters.pointOnAngle(prevPoint, anglePointStart, anglePointCenter, anglePointEnd, offset, sideCoefficient);

                paintPoints.push(nextPoint);

                prevPoint = nextPoint;
            }

            let endPoint = getters.endPoint(points[l-1], points[l-2], offset, sideCoefficient);
            paintPoints.push(endPoint);

            return paintPoints;
        },

        lineFormulaX: () => (y, p1, dx, dy) => {
            let x = p1.x + dx / dy * (y - p1.y);

            return x;
        },

        lineFormulaY: () => (x, p1, dx, dy) => {
            let y = p1.y + dy / dx * (x - p1.x);

            return y;
        },

        paintStartAngle(state, getters, rootState, rootGetters) {
            // startAngle is uses in the brush image rotation
            let points = rootState['editor'].points;

            return points.length ? rootGetters['line/getLineByPoints'](points[0], points[1]).angle360 : 0;
        },

    },

    mutations: {
        setPalette(state, palette) {
            state.palette = palette;
        },

        setPaint(state, paint) {
            state.currentPaint = paint;
        },

        setPaintSide(state, side) {
            state.paintSide = side;
        },

        setPaintEnabled(state, { point, enabled }) {
            point['paintEnabled'] = enabled;
        },

        setCachedBrush(state, {id, brush}) {
            state.cachedBrushes[id] = brush;
        },

    },

    actions: {
        initPalette({commit}) {
            // convert palette from the string to the array of items
            let palette = paletteConverter(paletteAsString);

            commit('setPalette', palette);
        },

        initPaintSide({rootState, rootGetters, dispatch}) {
            let points = rootState['editor'].points;
            let firstOriginalLine = rootGetters['line/getLineByPoints'](points[0], points[1]);

            // use (360 - angel) to get the opposite angle in the right triangle
            let angle = 360 - firstOriginalLine.angle360;

            // switch sin and cos for dx and dy because the right triangle angles (90 - angle)
            let dy = Math.cos(angle / 180 * Math.PI);
            // let dx = offset * Math.sin(angle / 180 * Math.PI);

            // set the print side relative to the trim
            let paintSide = (dy < 0);

            dispatch('setPaintSide', paintSide);
        },

        setCurrentPaint({getters, commit, dispatch}, paintId) {
            if (!paintId) {
                dispatch('resetCurrentPaint');

                return;
            }

            let paint = getters.getPaintById(paintId);

            commit('setPaint', paint);
        },

        resetCurrentPaint({commit}) {
            // reset to default yellow color to show paint side
            let paint = {
                id: 0,
                name: '',
                color: {R: 255, G: 255, B: 0},
            };

            commit('setPaint', paint);
        },

        setPaintSide({commit}, paintSide) {
            commit('setPaintSide', paintSide);
        },

        switchPaintSide({state, dispatch}) {
            let otherSide = !state.paintSide;

            dispatch('setPaintSide', otherSide);
        },

        saveBrush({state, commit}, {id, cachedBrush}) {
            let hasBrush = state.cachedBrushes[id];

            // check if the brush cache was saved before
            if (hasBrush) return;

            commit('setCachedBrush', { id, brush: cachedBrush });
        },

    }
}